import React, { FC, memo, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { LoadingSpinner } from "@/components/Loading";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IAssignment } from "@/data/assignments";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { fetchJobApplicationEnsure, IJobApplication } from "@/data/job-applications";
import { fetchJobRequest, IJobRequest } from "@/data/job-requests";
import { Flex } from "@/eworkitComponents/components";
import { useFetcher } from "@/hooks/http";
import { useRouter } from "@/router/useRouter";
import { AssignmentRelatedConsultantsList } from "@/views/Assignments/Details/components/AssignmentRelatedConsultantsList";
import { AssignmentRelatedConsultantsTable } from "@/views/Assignments/Details/components/AssignmentRelatedConsultantsTable";
import { router } from "@/views/router";

import { AssignmentRelatedJAList } from "./AssignmentRelatedJAList";
import { AssignmentRelatedJATable } from "./AssignmentRelatedJATable";
import { AssignmentRelatedJRList } from "./AssignmentRelatedJRList";
import { AssignmentRelatedJRTable } from "./AssignmentRelatedJRTable";

export interface IAssignmentRelatedProps {
  assignment: IAssignment;
}

export const AssignmentRelated: FC<IAssignmentRelatedProps> = memo(({ assignment }) => {
  const { t } = useTranslation("session");
  const { contextCompany } = useContext(UserContext);
  const { push } = useRouter();
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const { payload: jobRequest, working: jrWorking } = useFetcher(
    async () =>
      assignment.jobRequest &&
      (await fetchJobRequest({
        jobRequestId: assignment.jobRequest.id,
        companyId: contextCompany?.id,
      })),
    {},
  );

  const { payload: jobApplication, working: jaWorking } = useFetcher(
    async () =>
      assignment.jobRequestApplication &&
      (await fetchJobApplicationEnsure({
        jobApplicationId: assignment.jobRequestApplication.id,
        companyId: contextCompany?.id,
      })),
    {},
  );

  const handleJRRowClick = useCallback(
    (jr: IJobRequest) => push(router.get("job-requests.detail", { jobRequestId: jr.id }).path),
    [],
  );

  const handleJARowClick = useCallback(
    (ja: IJobApplication) => push(router.get("job-applications.details", { jobApplicationId: ja.id }).path),
    [],
  );

  const handleConsultantRowClick = useCallback(
    (cp: IConsultantProfile) =>
      push(router.get("consultant-profiles.detail", { consultantProfileId: cp.parent?.id ?? cp.id }).path),
    [],
  );

  return (
    <Flex direction="column" width={12} innerMargin="md">
      {(jrWorking || jobRequest) && (
        <Flex direction="column">
          <Text size="xl" weight="bold">
            {t("ASSIGNMENTS.DETAILS.RELATED_DETAILS.JOB_REQUESTS.TITLE")}
          </Text>

          {jrWorking && (
            <Flex margin={["xl", false]} align="center" justify="center">
              <LoadingSpinner size="lg" variant="primary" />
            </Flex>
          )}

          {!jrWorking && jobRequest && (
            <Flex margin={["md", false, false, false]}>
              {!isSmallScreen && <AssignmentRelatedJRTable jobRequest={jobRequest} onRowClick={handleJRRowClick} />}

              {isSmallScreen && <AssignmentRelatedJRList jobRequest={jobRequest} onRowClick={handleJRRowClick} />}
            </Flex>
          )}
        </Flex>
      )}

      {(jaWorking || jobApplication) && (
        <Flex direction="column">
          <Text size="xl" weight="bold">
            {t("ASSIGNMENTS.DETAILS.RELATED_DETAILS.JOB_APPLICATIONS.TITLE")}
          </Text>

          {jaWorking && (
            <Flex margin={["xl", false]} align="center" justify="center">
              <LoadingSpinner size="lg" variant="primary" />
            </Flex>
          )}

          {!jaWorking && jobApplication && (
            <Flex direction="column" margin={["md", false, false, false]}>
              {!isSmallScreen && (
                <AssignmentRelatedJATable jobApplication={jobApplication} onRowClick={handleJARowClick} />
              )}

              {isSmallScreen && (
                <AssignmentRelatedJAList jobApplication={jobApplication} onRowClick={handleJARowClick} />
              )}
            </Flex>
          )}
        </Flex>
      )}

      {assignment.consultantProfile && (
        <Flex direction="column">
          <Text size="xl" weight="bold">
            {t("ASSIGNMENTS.DETAILS.RELATED_DETAILS.CONSULTANTS.TITLE")}
          </Text>
          <Flex direction="column" margin={["md", false, false, false]}>
            {!isSmallScreen && (
              <AssignmentRelatedConsultantsTable
                consultantProfile={assignment.consultantProfile}
                onRowClick={handleConsultantRowClick}
              />
            )}

            {isSmallScreen && (
              <AssignmentRelatedConsultantsList
                consultantProfile={assignment.consultantProfile}
                onRowClick={handleConsultantRowClick}
              />
            )}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});
